import {
  displayDateTimeToMinute,
  Entity,
  FieldType,
  FileStorageType,
  PersistenceType,
  ReferenceField,
} from 'gossamer-unified';
import { UserView } from './UserView';
import {
  EmploymentGradeOptions,
  EventFlowOptions,
  EventTypeOptions,
  SpecialtyGroupOptions,
  VisibilityLimitOptions,
  YearGroupOptions,
} from './SharedOptions';
import { Specialty } from './Specialty';
import { FeedbackForm } from './FeedbackForm';
import { EventProfile } from './EventProfile';
import { BookingPaymentTerms } from './BookingPaymentTerms';
import { Location } from './Location';
import { ACCESSIBLE_EVENT_NODE_IDS, EVENTS_I_HOST, GROUPS_I_AM_MEMBER_OF } from './SecurityRules';
import { EventRepeatMetadata, EventRepetition, formatEventRepetition } from './EventRepetition';
import { InstitutionView } from './InstitutionView';

export interface EventRow {
  id: number;
  title: string;
  type: string;
  images: string;
  start_at: string;
  end_at: string;
  markdown_description: string;
  plain_description: string;

  main_host_user_id: string;
  location_type: string;
  location: string;
  gps_location: number | null;
  livestream_session_id: string;
  booking_method: string;
  booking_url: string;
  booking_payment_terms_id: number | null;
  specialty_group: string;
  specialties: string;
  employment_grade: string;
  year_group: string;

  institution_id: number;
  event_profile_id: number;
  visibility_limit: string;
  flow_option: string;
  feedback_form_id: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  attendance_token: string;
  search_vector: string;
  repeat_pattern: EventRepetition | null;
  repeat_start_event_id: number | null;
  repeat_group_id: string | null;
  repeat_metadata: EventRepeatMetadata | null;
}

// access conditions
const IS_PUBLIC_EVENT = "(visibility_limit = 'public')";
const IS_ACCESSIBLE_PRIVATE_EVENT = "(visibility_limit = 'private' AND institution_id IN ({accessInstitutionIds}))";
const IS_ACCESSIBLE_PRIVATE_GROUP_EVENT = `(visibility_limit = 'private-group' AND id IN (SELECT institution_event_id FROM public.institution_group_event WHERE institution_group_id IN ${GROUPS_I_AM_MEMBER_OF}))`;
const IS_ACCESSIBLE_ORG_UNIT_EVENT = `(id IN ${ACCESSIBLE_EVENT_NODE_IDS})`;
const IS_CLOSED_BOOKED_EVENT = `(visibility_limit = 'closed' AND id IN (SELECT event_id FROM public.ticket_booking WHERE status = 'active' AND user_id = '{userId}'))`;
const IS_HOST_OF = `(id IN ${EVENTS_I_HOST})`;
const IS_ADMIN_OF_INSTITUTION = 'institution_id = {adminInstitutionId}';

export const Event: Entity<EventRow> = {
  id: 'Event',
  title: 'Event',
  service: 'main',
  rowLabel: (row: EventRow) => row.title,
  primaryKeyFields: ['id'],
  source: {
    tableName: 'public.event',
  },
  security: [
    {
      role: 'none',
      action: 'read',
      access: 'conditional',
      condition: IS_PUBLIC_EVENT,
    },
    {
      role: 'user',
      action: 'read',
      access: 'conditional',
      condition: [
        IS_PUBLIC_EVENT,
        IS_ACCESSIBLE_PRIVATE_EVENT,
        IS_ACCESSIBLE_PRIVATE_GROUP_EVENT,
        // IS_ACCESSIBLE_ORG_UNIT_EVENT,
        IS_CLOSED_BOOKED_EVENT,
        IS_HOST_OF,
      ].join(' OR '),
    },
    {
      role: 'user',
      action: 'update',
      access: 'conditional',
      condition: IS_HOST_OF,
    },
    {
      role: 'admin',
      action: 'read',
      access: 'conditional',
      condition: [
        IS_PUBLIC_EVENT,
        IS_ACCESSIBLE_PRIVATE_EVENT,
        IS_ACCESSIBLE_PRIVATE_GROUP_EVENT,
        // IS_ACCESSIBLE_ORG_UNIT_EVENT,
        IS_CLOSED_BOOKED_EVENT,
        IS_HOST_OF,
        IS_ADMIN_OF_INSTITUTION,
      ].join(' OR '),
    },
    {
      role: 'admin',
      action: 'update',
      access: 'conditional',
      condition: [IS_HOST_OF, IS_ADMIN_OF_INSTITUTION].join(' OR '),
    },
    { role: 'staff', action: 'all', access: 'all' },
  ],
  defaultSort: [['title', 'ASC']],
  defaultFilters: [{ fieldId: 'title', operator: 'ct', value: '' }],
  // listRowLink: '/dashboard/hosted-events/{key}',
  refOrderClause: 'SELECT ZZ.title FROM public.event ZZ WHERE ZZ.id',
  listRowLink: '/admin/event/display/{key}',
  fields: {
    id: {
      seq: 100,
      type: FieldType.Integer,
      label: 'Id',
      persistence: PersistenceType.BackEndManaged,
      fieldGroup: 'basic',
      mandatory: true,
      listColumn: true,
      autoIncrement: true,
    },
    title: {
      seq: 110,
      type: FieldType.Text,
      label: 'Title',
      fieldGroup: 'basic',
      mandatory: true,
      listColumn: true,
    },
    type: {
      seq: 120,
      type: FieldType.Options,
      label: 'Type',
      fieldGroup: 'basic',
      mandatory: true,
      listColumn: true,
      listColumnWidth: 110,
      options: EventTypeOptions,
    },
    images: {
      seq: 130,
      type: FieldType.File,
      label: 'Images',
      fieldGroup: 'basic',
      imagesOnly: true,
      storageType: FileStorageType.ExternalResource,
      evaluateBackEndOut: (row: EventRow) => row.images?.[0], // can't deal with field values as arrays
    },
    start_at: {
      seq: 135,
      type: FieldType.Datetime,
      label: 'Start',
      fieldGroup: 'basic',
      listColumn: true,
      getDisplayText: (value: string) => displayDateTimeToMinute(value, '-'),
    },
    end_at: {
      seq: 140,
      type: FieldType.Datetime,
      label: 'End',
      fieldGroup: 'basic',
      listColumn: false,
      getDisplayText: (value: string) => displayDateTimeToMinute(value, '-'),
    },
    markdown_description: {
      seq: 145,
      type: FieldType.Text,
      label: 'Markdown Description',
      fieldGroup: 'basic',
      formColumnWidths: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12, xxl: 6 },
    },
    plain_description: {
      seq: 150,
      type: FieldType.Text,
      label: 'Plain Description',
      fieldGroup: 'basic',
      formColumnWidths: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12, xxl: 6 },
    },
    main_host_user_id: {
      seq: 160,
      type: FieldType.Reference,
      label: 'Main Host',
      fieldGroup: 'hosting',
      refEntity: UserView,
    },
    institution_id: {
      seq: 165,
      type: FieldType.Reference,
      label: 'Institution',
      fieldGroup: 'other-details',
      refEntity: InstitutionView, // allow all users to read
      mandatory: true,
      listColumn: false,
    },
    event_profile_id: {
      seq: 170,
      type: FieldType.Reference,
      label: 'Event Profile',
      fieldGroup: 'other-details',
      formColumnWidths: { md: 6 },
      refEntity: EventProfile,
      mandatory: false,
      listColumn: false,
    },
    visibility_limit: {
      seq: 175,
      type: FieldType.Options,
      label: 'Visibility Limit',
      fieldGroup: 'other-details',
      formColumnWidths: { md: 6 },
      mandatory: true,
      options: VisibilityLimitOptions,
    },
    flow_option: {
      seq: 180,
      label: 'Post-Event Flow',
      type: FieldType.Options,
      fieldGroup: 'other-details',
      formColumnWidths: { md: 6 },
      description: 'Whether to track attendance, capture feedback and issue certificate',
      options: EventFlowOptions,
    },
    feedback_form_id: {
      seq: 185,
      type: FieldType.Reference,
      label: 'Feedback Form',
      fieldGroup: 'other-details',
      refEntity: FeedbackForm,
    },
    location_type: {
      seq: 200,
      type: FieldType.Options,
      label: 'Location Type', // aka Hosting Method
      fieldGroup: 'other-details',
      mandatory: true,
      options: [
        {
          id: 'in-person',
          label: 'In person',
        },
        {
          id: 'online',
          label: 'Online',
        },
        {
          id: 'hybrid',
          label: 'Hybrid',
        },
      ],
    },
    location: {
      seq: 210,
      type: FieldType.Text,
      label: 'Location',
      fieldGroup: 'hosting',
    },
    gps_location: {
      seq: 215,
      type: FieldType.Reference,
      label: 'GPS Location',
      fieldGroup: 'other-details',
      refEntity: Location,
      mandatory: false,
      listColumn: false,
      description: 'Optional reference to a saved Location (with coordinates).',
    },
    livestream_session_id: {
      seq: 220,
      type: FieldType.Text,
      label: 'Livestream Session',
      fieldGroup: 'staff-view',
    },
    booking_method: {
      seq: 300,
      type: FieldType.Options,
      label: 'Booking Method',
      fieldGroup: 'other-details',
      mandatory: true,
      options: [
        { id: 'careerbook', label: 'Booking managed on careerbook' },
        { id: 'offsite', label: 'Booking managed off site' },
      ],
    },
    booking_url: {
      seq: 310,
      type: FieldType.Text,
      label: 'Booking Url',
      fieldGroup: 'staff-view',
    },
    booking_payment_terms_id: {
      seq: 315,
      type: FieldType.Reference,
      label: 'Booking Payment Terms',
      fieldGroup: 'staff-view',
      refEntity: BookingPaymentTerms,
    },
    specialty_group: {
      seq: 400,
      type: FieldType.Options,
      label: 'Specialty Group',
      fieldGroup: 'other-details',
      options: SpecialtyGroupOptions,
    },
    // TODO - child of specialty_group
    specialties: {
      seq: 410,
      type: FieldType.Reference,
      label: 'Specialties',
      fieldGroup: 'other-details',
      listColumn: false,
      sortable: false,
      filterable: false,
      refEntity: Specialty,
    },
    employment_grade: {
      seq: 420,
      type: FieldType.Options,
      label: 'Employment Grade',
      fieldGroup: 'other-details',
      options: EmploymentGradeOptions,
    },
    year_group: {
      seq: 430,
      type: FieldType.Options,
      label: 'Year Group',
      fieldGroup: 'other-details',
      options: YearGroupOptions,
    },
    created_by: {
      seq: 500,
      type: FieldType.Reference,
      label: 'Created By',
      refEntity: UserView,
      fieldGroup: 'audit-trail',
    },
    created_at: {
      seq: 510,
      label: 'Created At',
      type: FieldType.Datetime,
      persistence: PersistenceType.BackEndManaged,
      fieldGroup: 'audit-trail',
    },
    updated_at: {
      seq: 520,
      label: 'Last Updated At',
      type: FieldType.Datetime,
      persistence: PersistenceType.BackEndManaged,
      fieldGroup: 'audit-trail',
    },
    attendance_token: {
      seq: 540,
      type: FieldType.Text,
      label: 'Attendance Token',
    },
    search_vector: {
      seq: 560,
      type: FieldType.Text,
      label: 'Search Vector',
    },
    repeat_pattern: {
      seq: 570,
      type: FieldType.Object,
      label: 'Repeat Pattern',
      persistence: PersistenceType.BackEndManaged,
      fieldGroup: 'other-details',
      getDisplayText: formatEventRepetition,
    },
    repeat_start_event_id: {
      seq: 580,
      type: FieldType.Text,
      label: 'Repeat Start Event',
      persistence: PersistenceType.BackEndManaged,
      fieldGroup: 'staff-view',
    },
    repeat_group_id: {
      seq: 590,
      type: FieldType.Text,
      label: 'Repeat Group',
      persistence: PersistenceType.BackEndManaged,
      fieldGroup: 'staff-view',
    },
    repeat_metadata: {
      seq: 600,
      type: FieldType.Object,
      label: 'Repeat Metadata',
      persistence: PersistenceType.BackEndManaged,
      fieldGroup: 'staff-view',
    },
  },
};

const repeatStartEventField = Event.fields.repeat_start_event_id as ReferenceField;
repeatStartEventField.type = FieldType.Reference;
repeatStartEventField.refEntity = Event;
